<template>
  <form :id=id ref="form" @submit.prevent="submitForm" :class="formElementClass">
    <ul v-if="validationMessages.length > 0 && showMessageNotifications" :wrapperStyle="wrapperStyle" class="error-msgs">
      <li 
        v-for="(message, i) in validationMessages"
        :key="i"
        v-html="message"
      ></li>
    </ul>
    <ul v-if="hasErrorBetweenFields" class="error-msgs">
      <li v-if="hasDateOverlap">
        {{ this.$i18n.t('errors.quarter.dates_overlap') }}
      </li>
      <li v-if="hasDayAfterError">
        {{ this.$i18n.t('errors.quarter.day_after_error') }}
      </li>
    </ul>
    <div :class="[formWrapperClass ? formWrapperClass : 'flex-layout']">
      <div class="form-group" v-for="field in localInputFields"
           :key="field.id"
           :style="field.style"
           :class="[field.customClass, formGroupClass]" >
        <app-field-renderer
            :field="field"
            :model="model"
            :onBlurOnly="onBlurOnly"
            :useDebounce="useDebounce"
            @updateField="updateField"
            @updateModel="updateModel"
            @onBlur="onBlur"
            @saveModel="saveModel"
            @uploadComplete="uploadComplete"
            @onSearchChangeInput="onSearchChangeInput"
        >
          <template v-for="(_, slot) in $slots">
            <template :slot="slot">
              <slot :name="slot"></slot>
            </template>
          </template>
        </app-field-renderer>

      </div>
    </div>
    <div :class="['btn-container', showFooterBorderTop ? 'border-top mt-2 pt-3' : '']">
      <button
        v-if="showSubmit"
        :disabled="disableSubmitButton"
        :class="[ submitButtonClass, { loading: loading }]"
        class="btn"
        type="submit"
        @click.prevent="(e) => saveModel({e})"
      >
        <i v-if="loading" class="fas fa-spinner rotate" ></i>
        {{ submitButtonLabel }}
      </button>

      <input v-if="showSaveAs"
             type="button"
             :class="submitButtonClass"
             :disabled="hasErrorBetweenFields"
             :value=saveAsButtonLabel
             v-on:click="saveAsModel"/>
      <button 
        v-if="showDelete"
        class="input-ui-sm btn btn-carma btn-delete mt10 mr10"
        @click="deleteModel"
      >
        Delete
      </button>

      <input v-if="showCancel"
             type="button"
             class="input-ui-sm btn btn-default btn-cancel mt10"
             :value=cancelButtonLabel
             v-on:click="cancel"/>
    </div>
  </form>
</template>

<script>
import _ from 'lodash';
import helperDate from "../helpers/date";
import FormHelper from '../helpers/form';
import FieldRenderer from './form-components/field_renderer';
import { VALIDATIONS, FIELD_TYPE } from '../config/const';

export default {
  props: {
    id: String,
    customClass: Array,
    formElementClass: String,
    formWrapperClass: String,
    formGroupClass: String,
    inputFields: Object,
    customValidators: Object,
    validateFormTrigger: Boolean,
    validateFieldTrigger: {
      type: [Object, Boolean]
    },
    model: {
      type: Object,
      default: () => { return {} }
    },
    showSubmit: Boolean,
    disableSubmit: {
      type: Boolean,
      require: false,
      default: false,
    },
    submitButtonLabel: {
      type: String,
      default: 'Save'
    },
    submitButtonClass: {
      type: String,
      default: 'input-ui-sm btn btn-carma btn-save mt10'
    },
    showDelete: {
      type: Boolean,
      required: false,
      default: false
    },
    showSaveAs: Boolean,
    saveAsButtonLabel: {
      type: String,
      default: 'Save As'
    },
    showCancel: Boolean,
    cancelButtonLabel: {
      type: String,
      default: 'Cancel'
    },
    saveOnBlur: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      required: false,
      default: false
    },
    onBlurOnly: {
      type: Boolean,
      required: false,
      default: false
    },
    useDebounce: {
      type: Boolean,
      default: true
    },
    showMessageNotifications: Boolean,
    showFooterBorderTop: Boolean
  },
  components: {
    'app-field-renderer': FieldRenderer
  },
  watch: {
    validateFormTrigger(state) {
      if(state) {
        this.clearAllErrors();
        this.validationMessages = this.validateForm();
      }
    },

    validateFieldTrigger: {
      deep: true,
      handler(state) {
        if(state && this.$refs.form.id === state.formId) {

          if(state.field.group_fields) {
            state.field.group_fields.map(fieldKey => {
              let groupField = this.localInputFields.find(f => f.id === fieldKey || f.name === fieldKey);
              if(groupField) groupField.validated = false;
            });
          }

          this.validationMessages = this.validateField(state.field, state.value);
        }
      }
    }
  },
  computed: {
    localInputFields: {
      get() {
        if (!Object.values(this.inputFields).length) return [];

        return Object.values(this.inputFields).map((field, i) => {

          if (!field.id) field.id = `${field.name}_${i}`;

          if (field && field.validate && field.type === FIELD_TYPE.CALENDAR) {
            const noOverlap = this.getOverlap(field.validate);

            if (noOverlap) field.overlap = this.verifyOverlap(field, noOverlap);
            if (field.dayAfter)
              field.dayAfter.hasError = this.verifyDayAfterError(field);
          }
          
          return field;
        });
      },
      set(newValue) {
        this.$emit("input", newValue);
      }
    },
    disableSubmitButton() {
      return this.hasErrorBetweenFields ||
        this.disableSubmit ||
        this.loading;
    },
    hasDateOverlap() {
      const item = this.localInputFields.find(field => {
        return field.overlap && field.overlap.hasError
      });
      
      if (!item) return null;

      return item.overlap.hasError;
    },
    hasDayAfterError() {
      const item = this.localInputFields.find(field => {
        return field.dayAfter && field.dayAfter.hasError
      });
      
      if (!item) return null;

      return item.dayAfter.hasError;
    },
    hasErrorBetweenFields() {
      return this.hasDateOverlap || this.hasDayAfterError;
    }
  },

  mounted() {
    this.validationMessages = [];
  },

  methods: {
    getOverlap(validate) {
      const FieldNoOverlap = validate.find((item) => {
        return item.noOverlap;
      });

      if (FieldNoOverlap) return FieldNoOverlap.noOverlap;

      return null;
    },
    
    verifyOverlap(item, notOverlap) {
      let data = {
        hasError: false,
        input: [],
      };

      const inputValue = this.model[item.name];

      data.inputs = notOverlap.map((field) => {
        const notOverlapItem = this.model[field];

        if (
          inputValue && notOverlapItem && 
          (
            _.isArray(inputValue) && _.isArray(notOverlapItem) &&
            (Date.parse(inputValue[0]) >= Date.parse(notOverlapItem[0]) &&
              Date.parse(inputValue[0]) <= Date.parse(notOverlapItem[1])) ||
            (Date.parse(inputValue[1]) >= Date.parse(notOverlapItem[0]) &&
              Date.parse(inputValue[1]) <= Date.parse(notOverlapItem[1])) ||
            (Date.parse(inputValue[0]) <= Date.parse(notOverlapItem[0]) &&
              Date.parse(inputValue[1]) >= Date.parse(notOverlapItem[1]))
          ) ||
          ( 
            !_.isArray(inputValue) && !_.isArray(notOverlapItem) &&
            inputValue === notOverlapItem
          )
        ) {
          data.hasError = true;
        }
      });
      return data;
    },
    updateModel ({ model, field }) {
      this.clearAllErrors();

      if (field && field.type === FIELD_TYPE.CALENDAR)
        model = this.verifyDayAfter(model, field.name);

      this.$emit('updateModel', model, field);
      this.$emit('update-model', model);
    },
    verifyDayAfterError(field) {
      if (!field.dayAfter.required) return false;

      let fromData = this.model[field.dayAfter.from];

      const fromValue = new Date(_.isArray(fromData) ? fromData[1] : fromData);

      const fromValueDayAfter = new Date(fromValue.setDate(fromValue.getDate() + 1));

      const fieldValue = new Date(
        _.isArray(this.model[field.name])
          ? this.model[field.name][0]
          : this.model[field.name]
      );

      const newFieldValue = new Date(helperDate.dateToString(fieldValue));
      const newFromValueDayAfter = new Date(helperDate.dateToString(fromValueDayAfter));

      if (newFieldValue.getTime() === newFromValueDayAfter.getTime()) return false;

      return true;
    },
    verifyDayAfter(model, name) {
      const dayAfterFields = this.localInputFields.filter(field => {
        return field.dayAfter && field.dayAfter.from === name;
      });

      dayAfterFields.forEach(field => {
        const updatedDate = _.isArray(model[name]) 
          ? new Date(model[name][1])
          : new Date(model[name]);
        const dayAfter = new Date(updatedDate.setDate(updatedDate.getDate() + 1));
        const dayAfterString = helperDate.dateToString(dayAfter);

        if (field.dayAfter.auto) {
          let fieldValue =  _.isArray(model[field.name])
            ? model[field.name][0] = dayAfterString
            : model[field.name] = dayAfterString;
            
          if (_.isArray(model[field.name])) {
            model[field.name][0] = dayAfterString;
            const dayEnd = new Date(model[field.name][1]);
            if (dayAfter >= dayEnd) {
              const newdayEnd = new Date(dayAfter.setDate(dayAfter.getDate() + 1));
              model[field.name][1] = helperDate.dateToString(newdayEnd);
              model = this.verifyDayAfter(model, field.name);
            }
          } else {
            model[field.name] = dayAfterString;
            const dayEnd = new Date(model[field.name][1]);
            model = this.verifyDayAfter(model, field.name);
          }
        }

      });

      return model;
    },

    onBlur ({field, value}) {
      this.$emit('onFieldBlur', {field, value, formId: this.$refs.form.id});

      if(this.saveOnBlur && !field.blockGeneralUpdate) {
        this.saveModel({field, value});
      }
    },

    saveModel ({e, field, value}) {
      if(e) {
        e.stopPropagation();
        e.preventDefault();
      }

      this.clearAllErrors();
      let errors = [];

      if (!_.isUndefined(field) && !_.isUndefined(value)) {
        //Validate the modified value only
        errors = this.validateField(field, value);
      }
      else {
        errors = this.validateForm();
      }


      this.validationMessages = errors;
      if(errors.length === 0 && !this.hasErrorBetweenFields) {
        this.$emit('onFormSubmit');
        this.$emit('on-form-submit');
      }
    },

    saveAsModel () {
      this.clearAllErrors();
      this.validationMessages = this.validateForm();
      
      if(this.validationMessages.length === 0) {
        this.$emit('onFormSubmit', true);
        this.$emit('on-form-submit');
      }
    },

    deleteModel () {
      this.clearAllErrors();
      this.validationMessages = this.validateForm();

      if(this.validationMessages.length === 0) {
        this.$emit('onDelete', true);
        this.$emit('on-delete');
      }
    },

    checkFieldLabel(field){
      return field.translate_label ? this.$i18n.t(field.translate_label) : field.label;
    },

    resolveErrorMessage(message, field) {
      message = message.translate_message ? message.translate_message : message.message;
      
      if (typeof message === 'function') return message();

      return  message
    },

    validateForm () {
      let invalidFields = [];

      this.localInputFields.map(field => {
        const validation = this.validateField(field, this.model && this.model[field.name]);
        if(validation) {
          invalidFields = [...invalidFields, ...validation];
        }
      });
      this.$emit('validate', invalidFields);
      return invalidFields;
    },

    updateField({ model, field }) {
      this.clearAllErrors();
      this.validationMessages = this.validateField(field, model[field.name]);

      if (!this.validationMessages || !this.validationMessages.length)
        field.update(model);
    },

    validateField (field, value) {
      const options = {
        customValidators: this.customValidators,
        localInputFields: this.localInputFields,
        inputFields: this.inputFields
      }
      const errors = FormHelper.validateField(field, value, options);
      if (errors.length > 0 && this.saveOnBlur) this.focusErrorField(field);
      return errors;
    },
    onSearchChangeInput(value, field) {
      this.$emit('onSearchChangeInput', value, field);
    },
    arrayIsEmpty(arrayData) {
      if (!_.isArray(arrayData)) return false;
      if (!arrayData.length) return true;

      const emptyValues = arrayData.filter(item => {
        return !item;
      });

      if (emptyValues.length) return true;

      return false;
    },
    focusErrorField (field) {
      this.$refs.form.querySelector('#' + field.id).focus();
    },
    clearAllErrors () {
      this.validationMessages = [];
      
      this.localInputFields.map(f => {
        if (f.group_fields) f.validated = false;
      });

      const elementsWithError = this.$refs.form.getElementsByClassName('has-error');
      Array.from(elementsWithError).forEach(element => {
        let field = this.localInputFields.find(field => field.id === element.id);

        if (field.type === FIELD_TYPE.CALENDAR) {

          const field = this.localInputFields.find(item => {
            return field.name === item.name;
          })

          const wrapperClass = field.wrapperClass;

          if (wrapperClass)
            field.wrapperClass = wrapperClass.replace(' has-error', '');
        }
        else {
          element.classList.remove('has-error');
        }
      });
    },
    cancel () {
      this.$emit('onCancelClick');
    },
    submitForm (e) {
      if (this.showSubmit) {
        this.saveModel(e);
      }
    },
    uploadComplete() {
      this.$emit('uploadComplete')
    },

    areGroupFieldsEmpty (fields) {
      let queriesEmpty = true;
      fields.map(field => {
        if (!_.isUndefined(this.model[field]) && this.model[field].trim() !== '') {
          queriesEmpty = false;
        }
      });
      return queriesEmpty;
    }
  },
  data () {
    return {
      validationMessages: [],
      FIELD_TYPE: FIELD_TYPE,
      wrapperStyle: {
        position: 'absolute',
        top: '50%',
        left: '50%'
      },
      groupFailed: false
    }
  },

}
</script>

<style lang="scss">
@import "../styles/components/form";
</style>